import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ColaboradoresApi, UsuariosApi } from '../api/client.js';
import { useState, useMemo, useEffect } from 'react';
import { Table } from '../components/Table.jsx';
import { useToast } from '../components/ToastProvider.jsx';
import { PasswordInput } from '../components/PasswordInput.jsx';
import { useIsMobile } from '../hooks/useIsMobile.js';

export default function ColaboradoresPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const { data: resp, isLoading } = useQuery({ queryKey:['colaboradores'], queryFn: () => ColaboradoresApi.listar() });
  const colaboradores = resp?.data || resp || [];
  const [paginaAtual, setPaginaAtual] = useState(1);
  const itensPorPagina = 10;

  const totalPaginas = useMemo(() => {
    if (!Array.isArray(colaboradores) || colaboradores.length === 0) return 1;
    return Math.ceil(colaboradores.length / itensPorPagina);
  }, [colaboradores]);

  const colaboradoresPaginados = useMemo(() => {
    const inicio = (paginaAtual - 1) * itensPorPagina;
    const fim = inicio + itensPorPagina;
    return colaboradores.slice(inicio, fim);
  }, [colaboradores, paginaAtual]);

  useEffect(() => {
    if (paginaAtual > totalPaginas) setPaginaAtual(totalPaginas);
  }, [paginaAtual, totalPaginas]);

  // Modo criação direta de usuário
  const [editandoId,setEditandoId] = useState(null);
  const [usuarioId,setUsuarioId] = useState('');
  const [nome,setNome] = useState('');
  const [email,setEmail] = useState('');
  const [senha,setSenha] = useState('');

  const [cargo,setCargo] = useState('');
  const [setor,setSetor] = useState('');
  const [bonus,setBonus] = useState('');
  const [dataAdmissao,setDataAdmissao] = useState('');
  const [dataDemissao,setDataDemissao] = useState('');

  const salvar = useMutation({ mutationFn: async () => {
    try {
      if (editandoId) {
        const dados = {
          cargo,
          setor,
          bonus_mensal_padrao: bonus || undefined,
          data_admissao: dataAdmissao || undefined,
          data_demissao: dataDemissao || undefined
        };
        const colab = await ColaboradoresApi.atualizar(editandoId, dados);
        toast.success('Colaborador atualizado');
        return colab;
      } else {
        const u = await UsuariosApi.criar({ nome, email, senha, perfil: 'COLABORADOR' });
        const colab = await ColaboradoresApi.criar({ 
          usuarioId: u.id, 
          cargo, 
          setor, 
          bonus_mensal_padrao: bonus || undefined,
          data_admissao: dataAdmissao || undefined,
          data_demissao: dataDemissao || undefined
        });
        toast.success('Colaborador criado');
        return colab;
      }
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || (editandoId ? 'Erro na atualização' : 'Erro na criação');
      toast.error(msg);
      throw e;
    }
  }, onSuccess: ()=>{
    setEditandoId(null);
    setUsuarioId(''); setCargo(''); setSetor(''); setBonus('');
    setDataAdmissao(''); setDataDemissao('');
    setNome(''); setEmail(''); setSenha('');
    qc.invalidateQueries({queryKey:['colaboradores']});
    qc.invalidateQueries({queryKey:['usuarios']});
  } });

  const isMobile = useIsMobile();

  const handleEditar = (colab) => {
    setEditandoId(colab.id);
    setUsuarioId(colab.usuario?.id || colab.usuarioId || '');
    setNome(colab.usuario?.nome || '');
    setEmail(colab.usuario?.email || '');
    setCargo(colab.cargo || '');
    setSetor(colab.setor || '');
    setBonus(colab.bonus_mensal_padrao || '');
    setDataAdmissao(colab.data_admissao ? colab.data_admissao.split('T')[0] : '');
    setDataDemissao(colab.data_demissao ? colab.data_demissao.split('T')[0] : '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelar = () => {
    setEditandoId(null);
    setUsuarioId(''); setCargo(''); setSetor(''); setBonus('');
    setDataAdmissao(''); setDataDemissao('');
    setNome(''); setEmail(''); setSenha('');
  };

  const columns = useMemo(()=> ([
    { key:'usuario', header:'Nome', render:(_,row)=> row.usuario?.nome },
    { key:'usuarioEmail', header:'Email', render:(_,row)=> row.usuario?.email },
    { key:'usuarioPerfil', header:'Perfil', render:(_,row)=> row.usuario?.perfil },
    { key:'cargo', header:'Cargo' },
    { key:'setor', header:'Setor' },
    { key:'bonus_mensal_padrao', header:'Bônus Mensal', render:v => v ? `R$ ${Number(v).toFixed(2)}` : '-' },
    { key:'data_admissao', header:'Data Admissão', render:v => v ? new Date(v).toLocaleDateString('pt-BR') : '-' },
    { key:'data_demissao', header:'Data Demissão', render:v => v ? new Date(v).toLocaleDateString('pt-BR') : '-' },
    { key:'acoes', header:'Ações', render:(_,row)=> (
      <button className="btn" onClick={()=>handleEditar(row)} style={{fontSize:'.7rem', padding:'.25rem .5rem'}}>Editar</button>
    )},
  ]), []);

  return (
    <div>
      <h2 className="page-title">Colaboradores</h2>
      <div className="card" style={{marginBottom:'1rem'}}>
        <form onSubmit={e=>{e.preventDefault(); salvar.mutate();}} style={{display:'flex', flexDirection:'column', gap:'.75rem'}}>
          {editandoId && <div style={{padding:'.5rem', background:'#fff3cd', borderRadius:'4px', fontSize:'.75rem', fontWeight:600}}>Modo Edição - ID: {editandoId}</div>}
          <div className="flex gap-sm" style={{flexWrap:'wrap', alignItems:'flex-end'}}>
            <input placeholder="Nome" value={nome} onChange={e=>setNome(e.target.value)} required={!editandoId} disabled={!!editandoId} style={{flex:'2 1 140px'}} />
            <input placeholder="Email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required={!editandoId} disabled={!!editandoId} style={{flex:'2 1 160px'}} />
            {!editandoId && <PasswordInput placeholder="Senha" value={senha} onChange={e=>setSenha(e.target.value)} required style={{flex:'1 1 120px'}} />}
          </div>
          <div className="flex gap-sm" style={{flexWrap:'wrap', alignItems:'flex-end'}}>
            <select value={cargo} onChange={e=>setCargo(e.target.value)} required style={{flex:'2 1 120px'}}>
              <option value="">Selecione o cargo...</option>
              <option value="AJUDANTE DE PATIO">AJUDANTE DE PATIO</option>
              <option value="OPERADOR EM GERAL">OPERADOR EM GERAL</option>
              <option value="MOTORISTA">MOTORISTA</option>
              <option value="ENCARREGADOS E LÍDERES">ENCARREGADOS E LÍDERES</option>
            </select>
            <input placeholder="Setor" value={setor} onChange={e=>setSetor(e.target.value)} required style={{flex:'2 1 120px'}} />
            <input placeholder="Bônus (R$)" type="number" step="0.01" value={bonus} onChange={e=>setBonus(e.target.value)} style={{flex:'1 1 110px'}} />
            <div style={{display:'flex', flexDirection:'column', gap:'.25rem', flex:'1 1 140px'}}>
              <label style={{fontSize:'.6rem', fontWeight:600}}>Data Admissão</label>
              <input type="date" value={dataAdmissao} onChange={e=>setDataAdmissao(e.target.value)} style={{fontSize:'.7rem'}} />
            </div>
            {editandoId && (
              <div style={{display:'flex', flexDirection:'column', gap:'.25rem', flex:'1 1 140px'}}>
                <label style={{fontSize:'.6rem', fontWeight:600}}>Data Demissão</label>
                <input type="date" value={dataDemissao} onChange={e=>setDataDemissao(e.target.value)} style={{fontSize:'.7rem'}} />
              </div>
            )}
          </div>
          <div style={{display:'flex', justifyContent:'flex-end', gap:'.5rem'}}>
            {editandoId && <button type="button" className="btn" onClick={handleCancelar} style={{background:'#6c757d'}}>Cancelar</button>}
            <button className="btn" disabled={salvar.isPending}>{salvar.isPending? 'Salvando...' : (editandoId ? 'Atualizar' : 'Criar')}</button>
          </div>
        </form>
        <p className="muted" style={{display:'flex', justifyContent:'flex-end', fontSize:'.6rem', marginTop:'.5rem'}}>Ao criar novo usuário o perfil será COLABORADOR automaticamente.</p>
      </div>
      <div className="card">
        {isLoading ? <p>Carregando...</p> : (
          isMobile ? (
            <div className="collab-cards">
              {colaboradoresPaginados.map(c => (
                <div className="collab-card" key={c.id}>
                  <div className="collab-card-header">{c.usuario?.nome || '(sem nome)'}</div>
                  <table className="mini-table">
                    <tbody>
                      <tr><th>Email</th><td>{c.usuario?.email}</td></tr>
                      <tr><th>Perfil</th><td>{c.usuario?.perfil}</td></tr>
                      <tr><th>Cargo</th><td>{c.cargo}</td></tr>
                      <tr><th>Setor</th><td>{c.setor}</td></tr>
                      <tr><th>Bônus</th><td>{c.bonus_mensal_padrao ? `R$ ${Number(c.bonus_mensal_padrao).toFixed(2)}` : '-'}</td></tr>
                      <tr><th>Data Admissão</th><td>{c.data_admissao ? new Date(c.data_admissao).toLocaleDateString('pt-BR') : '-'}</td></tr>
                      <tr><th>Data Demissão</th><td>{c.data_demissao ? new Date(c.data_demissao).toLocaleDateString('pt-BR') : '-'}</td></tr>
                      <tr><th>Ações</th><td><button className="btn" onClick={()=>handleEditar(c)} style={{fontSize:'.7rem', padding:'.25rem .5rem'}}>Editar</button></td></tr>
                    </tbody>
                  </table>
                </div>
              ))}
              {colaboradoresPaginados.length===0 && <p style={{fontSize:'.7rem'}}>Nenhum registro</p>}
            </div>
          ) : (
            <Table columns={columns} data={colaboradoresPaginados} keyField="id" />
          )
        )}
        {!isLoading && colaboradores.length > 0 && (
          <div style={{display:'flex', justifyContent:'space-between', alignItems:'center', gap:'.5rem', marginTop:'.75rem', flexWrap:'wrap'}}>
            <p className="muted" style={{fontSize:'.7rem', margin:0}}>
              Exibindo {(paginaAtual - 1) * itensPorPagina + 1} a {Math.min(paginaAtual * itensPorPagina, colaboradores.length)} de {colaboradores.length}
            </p>
            <div style={{display:'flex', gap:'.4rem', flex:'1 1 auto', justifyContent:'center'}}>
              <button
                type="button"
                className="btn"
                onClick={() => setPaginaAtual(p => Math.max(1, p - 1))}
                disabled={paginaAtual === 1}
                aria-label="Página anterior"
                title="Página anterior"
                style={{fontSize:'.7rem', padding:'.25rem .5rem'}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M15 6L9 12L15 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
              <button
                type="button"
                className="btn"
                onClick={() => setPaginaAtual(p => Math.min(totalPaginas, p + 1))}
                disabled={paginaAtual >= totalPaginas}
                aria-label="Próxima página"
                title="Próxima página"
                style={{fontSize:'.7rem', padding:'.25rem .5rem'}}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M9 6L15 12L9 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
